import styled from 'styled-components'
import { FitpetButton } from '@/components/common/buttons/fitpet-button'
import { Typo } from '@/components/common/typography'
import { ModalPolicy } from '@/components/domains/modals'
import PolicyKcp from '@/components/domains/policy/PolicyKcp'
import PolicyPrivacyPurpose from '@/components/domains/policy/PolicyPrivacyPurpose'
import PolicyProvide3rd from '@/components/domains/policy/PolicyProvide3rd'
import PolicyTossPayments from '@/components/domains/policy/PolicyTossPayments'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { useModal } from '@/containers/hooks'
import { currencyText } from '@/utils/utilNumber'

const agreementList = [
  { id: 1, title: '개인 정보 수집 및 이용 동의', content: <PolicyPrivacyPurpose /> },
  {
    id: 2,
    title: '결제대행 서비스 이용약관 동의',
    subContents: [
      { title: '토스페이먼츠', content: <PolicyTossPayments /> },
      { title: 'KCP', content: <PolicyKcp /> },
    ],
  },
  { id: 3, title: '구매 조건 및 개인 정보 제3자 제공 동의', content: <PolicyProvide3rd routeType={'replace'} /> },
]

interface OrderPaymentSectionProps {
  /** 최종 결제 금액 (productAmount + shippingAmount - couponDiscountAmount - mileageDiscountAmount) */
  amount: number
  /** 결제 진행 중 버튼 비활성화 */
  isSubmitting?: boolean
}

export const OrderPaymentSection = ({ amount, isSubmitting = false }: OrderPaymentSectionProps) => {
  const { useModalProps } = useModal()

  const handleClickAgreementButton = ({
    title,
    content,
    subContents,
  }: {
    title: string
    content?: JSX.Element
    subContents?: { title: string; content: JSX.Element }[]
  }) => {
    useModalProps.setData({ title, content, subContents })
    useModalProps.showModal()
  }

  return (
    <>
      <StyledSectionTitle>
        <Typo variant="Body1 Bold">주문 내용 확인 및 결제 동의</Typo>
      </StyledSectionTitle>
      <StyledSectionBody>
        <StyledAgreementList>
          {agreementList.map(({ id, title, content, subContents }) => (
            <StyledAgreementRow key={id}>
              <Typo variant="Body3 Regular" color={GlobalColorEnum.Gray400}>
                {title}
              </Typo>
              <StyledAgreementButton
                type="button"
                onClick={() => handleClickAgreementButton({ title, content, subContents })}
              >
                <Typo
                  variant="Body3 Regular"
                  color={GlobalColorEnum.Gray400}
                  style={{ textDecorationLine: 'underline' }}
                >
                  보기
                </Typo>
              </StyledAgreementButton>
            </StyledAgreementRow>
          ))}
        </StyledAgreementList>
        <StyledAgreementText>
          <Typo variant="Body5 Regular" color={GlobalColorEnum.Gray400}>
            위 상품의 구매 조건을 확인하였으며, 결제 및 개인 정보 제3자 제공에 모두 동의합니다.
          </Typo>
        </StyledAgreementText>
        <FitpetButton type="submit" variant="primary" width="full" disabled={isSubmitting}>
          <FitpetButton.ButtonText>{`${currencyText(amount)} 결제하기`}</FitpetButton.ButtonText>
        </FitpetButton>
      </StyledSectionBody>
      <ModalPolicy useModalProps={useModalProps} />
    </>
  )
}

const StyledSectionTitle = styled.div`
  height: 4.8rem;
  padding: 1.2rem 1.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledSectionBody = styled.div`
  padding: 0 1.6rem;
  margin-bottom: 4rem;
`

const StyledAgreementList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  background-color: ${(props) => props.theme.color.gray50};
  padding: 1.2rem;
  border-radius: 0.8rem;
`

const StyledAgreementRow = styled.div`
  display: flex;
  justify-content: space-between;
`

const StyledAgreementButton = styled.button`
  border: none;
  background-color: transparent;
  cursor: pointer;
`

const StyledAgreementText = styled.div`
  padding: 1.2rem 0;
`
